/**
 * 감사 로그 (API Audit Log) 목록 페이지
 * - 다양한 필터 + 페이지네이션
 * - 상단 통계 위젯 (총/성공/실패)
 * - 행 클릭 시 상세 모달 오픈
 *
 * 백엔드 명세: ADMIN_AUDIT_LOG_API.md
 */

import { useEffect, useMemo, useState } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  type AuditLogListItem,
  type AuditLogSearchRequest,
  type ApiActorType,
  type SuccessYn,
} from '@/services/api';
import { useAuditLogs, useAuditLogStats } from '@/hooks/useAuditLogs';
import {
  PageHeader,
  FilterBar,
  FilterField,
  TextInput,
  Select,
  Pagination,
  StatCard,
  StatGrid,
} from '@/components/common';
import {
  DataGrid,
  RowNumberCell,
  TruncatedTextCell,
  makeBadgeCell,
} from '@/components/grid';
import type { BadgeTone } from '@/components/common';
import { AuditLogDetailModal } from './AuditLogDetailModal';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const ACTOR_OPTIONS: { value: ApiActorType | ''; label: string }[] = [
  { value: '', label: '전체 액터' },
  { value: 'GUARDIAN', label: '보호자' },
  { value: 'TEACHER', label: '선생님' },
  { value: 'BUSINESS', label: '사업주' },
  { value: 'ADMIN', label: '어드민' },
  { value: 'ANONYMOUS', label: '비로그인' },
];

const METHOD_OPTIONS = ['', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const SUCCESS_OPTIONS: { value: SuccessYn | ''; label: string }[] = [
  { value: '', label: '성공/실패' },
  { value: 'Y', label: '성공만' },
  { value: 'N', label: '실패만' },
];

const ACTOR_LABEL: Record<ApiActorType, string> = {
  GUARDIAN: '보호자',
  TEACHER: '선생님',
  BUSINESS: '사업주',
  ADMIN: '어드민',
  ANONYMOUS: '비로그인',
};

const ACTOR_TONE: Record<ApiActorType, BadgeTone> = {
  GUARDIAN: 'info',
  TEACHER: 'primary',
  BUSINESS: 'warning',
  ADMIN: 'success',
  ANONYMOUS: 'neutral',
};

const SUCCESS_TONE: Record<SuccessYn, BadgeTone> = {
  Y: 'success',
  N: 'danger',
};

const SUCCESS_LABEL: Record<SuccessYn, string> = {
  Y: '성공',
  N: '실패',
};

const METHOD_TONE: Record<string, BadgeTone> = {
  GET: 'info',
  POST: 'primary',
  PUT: 'warning',
  PATCH: 'warning',
  DELETE: 'danger',
};

/**
 * 'YYYY-MM-DDTHH:mm:ss' 포맷으로 ISO 변환 (KST 그대로)
 * - <input type="datetime-local"> 값은 'YYYY-MM-DDTHH:mm' 이므로 보정
 */
function toIsoLocal(value: string): string | undefined {
  if (!value) return undefined;
  // 'YYYY-MM-DDTHH:mm' → 'YYYY-MM-DDTHH:mm:00'
  return value.length === 16 ? `${value}:00` : value;
}

/**
 * 오늘 00:00 ~ 내일 00:00 의 기본 기간 (KST)
 */
function getDefaultRange(): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const ty = tomorrow.getFullYear();
  const tm = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const td = String(tomorrow.getDate()).padStart(2, '0');
  return {
    from: `${y}-${m}-${d}T00:00`,
    to: `${ty}-${tm}-${td}T00:00`,
  };
}

interface AppliedFilters {
  dtFrom?: string;
  dtTo?: string;
  actorType?: ApiActorType;
  successYn?: SuccessYn;
  httpMethod?: string;
  httpStatus?: number;
  uriContains?: string;
  userSeq?: number;
  bizUserSeq?: number;
  companySeq?: number;
  petSeq?: number;
  ticketSeq?: number;
  /** 이름 LIKE 검색 (v1.2) */
  userName?: string;
  bizUserName?: string;
  companyName?: string;
  petName?: string;
  errorCode?: string;
  traceId?: string;
}

/**
 * 감사 로그 목록 페이지
 */
export function AuditLogListPage() {
  const defaultRange = useMemo(getDefaultRange, []);

  // ─── 로컬 폼 상태 ───
  const [localDtFrom, setLocalDtFrom] = useState(defaultRange.from);
  const [localDtTo, setLocalDtTo] = useState(defaultRange.to);
  const [localActor, setLocalActor] = useState<ApiActorType | ''>('');
  const [localSuccess, setLocalSuccess] = useState<SuccessYn | ''>('');
  const [localMethod, setLocalMethod] = useState<string>('');
  const [localUriContains, setLocalUriContains] = useState('');
  const [localHttpStatus, setLocalHttpStatus] = useState('');
  const [localUserSeq, setLocalUserSeq] = useState('');
  const [localBizUserSeq, setLocalBizUserSeq] = useState('');
  const [localCompanySeq, setLocalCompanySeq] = useState('');
  const [localPetSeq, setLocalPetSeq] = useState('');
  const [localTicketSeq, setLocalTicketSeq] = useState('');
  // 이름 LIKE 검색 (v1.2)
  const [localUserName, setLocalUserName] = useState('');
  const [localBizUserName, setLocalBizUserName] = useState('');
  const [localCompanyName, setLocalCompanyName] = useState('');
  const [localPetName, setLocalPetName] = useState('');
  const [localErrorCode, setLocalErrorCode] = useState('');
  const [localTraceId, setLocalTraceId] = useState('');
  const [localSize, setLocalSize] = useState(20);

  // ─── 적용된 필터 (실제 쿼리에 사용) ───
  const [applied, setApplied] = useState<AppliedFilters>({
    dtFrom: toIsoLocal(defaultRange.from),
    dtTo: toIsoLocal(defaultRange.to),
  });
  const [pageIndex, setPageIndex] = useState(0);
  const [size, setSize] = useState(20);
  /**
   * 검색 nonce — 동일한 필터 조건으로 다시 "검색" 버튼을 눌러도
   * 무조건 재호출되도록 queryKey 에 포함시키는 카운터.
   * CS 업무상 동일 조건 재조회 요구가 빈번해서 필수.
   */
  const [searchNonce, setSearchNonce] = useState(0);

  // 상세 모달
  const [detailLogSeq, setDetailLogSeq] = useState<number | null>(null);

  // 검색 요청 객체
  const searchBody = useMemo<AuditLogSearchRequest>(
    () => ({
      ...applied,
      page: pageIndex,
      size,
    }),
    [applied, pageIndex, size],
  );

  const { data, isLoading, isFetching, error, refetch } = useAuditLogs(
    searchBody,
    searchNonce,
  );

  // 통계 (현재 기간 조건 기준) — 동일 검색 시 재조회되도록 nonce 동기 적용
  const { data: stats } = useAuditLogStats(
    {
      dtFrom: applied.dtFrom,
      dtTo: applied.dtTo,
      topN: 5,
    },
    searchNonce,
  );

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const startIndex = pageIndex * size;

  // 새 필터 적용 시 1페이지로 이동
  useEffect(() => {
    setPageIndex(0);
  }, [applied, size]);

  const applyFilters = () => {
    const num = (s: string): number | undefined => {
      if (!s.trim()) return undefined;
      const n = Number(s.trim());
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };
    setApplied({
      dtFrom: toIsoLocal(localDtFrom),
      dtTo: toIsoLocal(localDtTo),
      actorType: localActor || undefined,
      successYn: localSuccess || undefined,
      httpMethod: localMethod || undefined,
      httpStatus: num(localHttpStatus),
      uriContains: localUriContains.trim() || undefined,
      userSeq: num(localUserSeq),
      bizUserSeq: num(localBizUserSeq),
      companySeq: num(localCompanySeq),
      petSeq: num(localPetSeq),
      ticketSeq: num(localTicketSeq),
      userName: localUserName.trim() || undefined,
      bizUserName: localBizUserName.trim() || undefined,
      companyName: localCompanyName.trim() || undefined,
      petName: localPetName.trim() || undefined,
      errorCode: localErrorCode.trim() || undefined,
      traceId: localTraceId.trim() || undefined,
    });
    setSize(localSize);
    // 동일 조건이어도 무조건 재호출되도록 nonce 증가
    setSearchNonce(n => n + 1);
  };

  const resetFilters = () => {
    setLocalDtFrom(defaultRange.from);
    setLocalDtTo(defaultRange.to);
    setLocalActor('');
    setLocalSuccess('');
    setLocalMethod('');
    setLocalUriContains('');
    setLocalHttpStatus('');
    setLocalUserSeq('');
    setLocalBizUserSeq('');
    setLocalCompanySeq('');
    setLocalPetSeq('');
    setLocalTicketSeq('');
    setLocalUserName('');
    setLocalBizUserName('');
    setLocalCompanyName('');
    setLocalPetName('');
    setLocalErrorCode('');
    setLocalTraceId('');
    setLocalSize(20);
    setApplied({
      dtFrom: toIsoLocal(defaultRange.from),
      dtTo: toIsoLocal(defaultRange.to),
    });
    setSize(20);
    // 초기화도 동일 조건일 수 있으니 nonce 증가
    setSearchNonce(n => n + 1);
  };

  // 컬럼 정의
  const columns = useMemo<ColDef<AuditLogListItem>[]>(
    () => [
      {
        headerName: '번호',
        width: 72,
        sortable: false,
        cellRenderer: RowNumberCell,
        cellRendererParams: { startIndex },
      },
      {
        headerName: '요청시각',
        field: 'dtRequest',
        width: 160,
        valueFormatter: p => (p.value ? String(p.value).replace('T', ' ') : '-'),
      },
      {
        headerName: 'Method',
        field: 'httpMethod',
        width: 90,
        cellRenderer: makeBadgeCell({
          toneMap: METHOD_TONE,
          defaultTone: 'neutral',
        }),
      },
      {
        /**
         * API 컬럼 — v1.4 apiSummary 우선 표시
         * - 1행: apiSummary (가독용 라벨, 굵게). null 이면 URL 만 표시.
         * - 2행: URL (작게, 회색)
         * - 둘 다 truncate + title 로 hover 시 풀텍스트
         */
        headerName: 'API',
        flex: 1,
        minWidth: 260,
        // 2줄 표시를 위해 기본 cellStyle 의 alignItems:center 를 stretch 로 덮어쓰기
        cellStyle: { display: 'flex', alignItems: 'stretch', padding: '4px 12px' },
        valueGetter: p => p.data?.apiSummary ?? p.data?.requestUri ?? '',
        cellRenderer: (params: ICellRendererParams<AuditLogListItem>) => {
          const row = params.data;
          if (!row) return null;
          const hasSummary = !!row.apiSummary;
          return (
            <div className="flex min-w-0 flex-col justify-center leading-tight">
              <div
                className={`truncate ${hasSummary ? 'text-[13px] font-medium text-gray-900' : 'font-mono text-xs text-gray-800'}`}
                title={hasSummary ? row.apiSummary! : row.requestUri}
              >
                {hasSummary ? row.apiSummary : row.requestUri}
              </div>
              {hasSummary && (
                <div
                  className="truncate font-mono text-[11px] text-gray-500"
                  title={row.requestUri}
                >
                  {row.requestUri}
                </div>
              )}
            </div>
          );
        },
      },
      {
        headerName: '상태',
        field: 'successYn',
        width: 80,
        cellRenderer: makeBadgeCell({
          toneMap: SUCCESS_TONE,
          labelMap: SUCCESS_LABEL,
        }),
      },
      {
        headerName: 'HTTP',
        field: 'httpStatus',
        width: 80,
      },
      {
        headerName: '소요',
        field: 'durationMs',
        width: 80,
        valueFormatter: p => (p.value != null ? `${p.value}ms` : '-'),
      },
      {
        headerName: '액터',
        field: 'cdActorType',
        width: 100,
        cellRenderer: makeBadgeCell({
          toneMap: ACTOR_TONE,
          labelMap: ACTOR_LABEL,
        }),
      },
      {
        headerName: '주체',
        width: 140,
        valueGetter: p => {
          const r = p.data;
          if (!r) return '';
          if (r.userName) return `${r.userName} (#${r.userSeq})`;
          if (r.bizUserName) return `${r.bizUserName} (#${r.bizUserSeq})`;
          if (r.userSeq) return `#${r.userSeq}`;
          if (r.bizUserSeq) return `#${r.bizUserSeq}`;
          return '-';
        },
        cellRenderer: TruncatedTextCell,
      },
      {
        headerName: '사업장',
        width: 140,
        valueGetter: p => {
          const r = p.data;
          if (!r) return '';
          if (r.companyName) return `${r.companyName} (#${r.companySeq})`;
          if (r.companySeq) return `#${r.companySeq}`;
          return '-';
        },
        cellRenderer: TruncatedTextCell,
      },
      {
        headerName: '에러코드',
        field: 'errorCode',
        width: 140,
        cellRenderer: TruncatedTextCell,
      },
    ],
    [startIndex],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="API 감사 로그"
        subtitle="모든 API 요청·응답 추적 로그 — 보호자/사업장/펫/티켓 단위로 조회할 수 있습니다."
      />

      {/* 통계 위젯 */}
      {stats && (
        <StatGrid cols={3}>
          <StatCard
            label="총 요청"
            value={stats.totalCount.toLocaleString()}
            tone="default"
          />
          <StatCard
            label="성공"
            value={stats.successCount.toLocaleString()}
            tone="success"
          />
          <StatCard
            label="실패"
            value={stats.failureCount.toLocaleString()}
            tone="danger"
            sub={
              stats.topErrorUris[0]
                ? `Top: ${stats.topErrorUris[0].httpMethod} ${stats.topErrorUris[0].uriPattern} (${stats.topErrorUris[0].count})`
                : undefined
            }
          />
        </StatGrid>
      )}

      {/* 필터 */}
      <FilterBar onSubmit={applyFilters} onReset={resetFilters}>
        <FilterField label="기간 시작" flex="flex-none" className="md:w-56">
          <TextInput
            type="datetime-local"
            value={localDtFrom}
            onChange={e => setLocalDtFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label="기간 종료" flex="flex-none" className="md:w-56">
          <TextInput
            type="datetime-local"
            value={localDtTo}
            onChange={e => setLocalDtTo(e.target.value)}
          />
        </FilterField>
        <FilterField label="액터" flex="flex-none" className="md:w-32">
          <Select
            value={localActor}
            onChange={e =>
              setLocalActor(e.target.value as ApiActorType | '')
            }
          >
            {ACTOR_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FilterField>
        <FilterField label="성공여부" flex="flex-none" className="md:w-28">
          <Select
            value={localSuccess}
            onChange={e =>
              setLocalSuccess(e.target.value as SuccessYn | '')
            }
          >
            {SUCCESS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FilterField>
        <FilterField label="Method" flex="flex-none" className="md:w-28">
          <Select
            value={localMethod}
            onChange={e => setLocalMethod(e.target.value)}
          >
            {METHOD_OPTIONS.map(m => (
              <option key={m} value={m}>
                {m || '전체'}
              </option>
            ))}
          </Select>
        </FilterField>
        <FilterField label="URI 포함" flex="flex-1" className="md:min-w-[200px]">
          <TextInput
            placeholder="/api/user/reserve 같이 부분 일치"
            value={localUriContains}
            onChange={e => setLocalUriContains(e.target.value)}
          />
        </FilterField>
        <FilterField label="HTTP 상태" flex="flex-none" className="md:w-24">
          <TextInput
            type="number"
            placeholder="400"
            value={localHttpStatus}
            onChange={e => setLocalHttpStatus(e.target.value)}
          />
        </FilterField>
        <FilterField label="보호자 이름" flex="flex-none" className="md:w-36">
          <TextInput
            placeholder="김보호"
            value={localUserName}
            onChange={e => setLocalUserName(e.target.value)}
          />
        </FilterField>
        <FilterField label="userSeq" flex="flex-none" className="md:w-24">
          <TextInput
            type="number"
            value={localUserSeq}
            onChange={e => setLocalUserSeq(e.target.value)}
          />
        </FilterField>
        <FilterField label="사업자/선생님 이름" flex="flex-none" className="md:w-40">
          <TextInput
            placeholder="김원장"
            value={localBizUserName}
            onChange={e => setLocalBizUserName(e.target.value)}
          />
        </FilterField>
        <FilterField label="bizUserSeq" flex="flex-none" className="md:w-24">
          <TextInput
            type="number"
            value={localBizUserSeq}
            onChange={e => setLocalBizUserSeq(e.target.value)}
          />
        </FilterField>
        <FilterField label="사업장명" flex="flex-none" className="md:w-40">
          <TextInput
            placeholder="멍투데이호텔"
            value={localCompanyName}
            onChange={e => setLocalCompanyName(e.target.value)}
          />
        </FilterField>
        <FilterField label="companySeq" flex="flex-none" className="md:w-24">
          <TextInput
            type="number"
            value={localCompanySeq}
            onChange={e => setLocalCompanySeq(e.target.value)}
          />
        </FilterField>
        <FilterField label="반려동물 이름" flex="flex-none" className="md:w-36">
          <TextInput
            placeholder="보리"
            value={localPetName}
            onChange={e => setLocalPetName(e.target.value)}
          />
        </FilterField>
        <FilterField label="petSeq" flex="flex-none" className="md:w-24">
          <TextInput
            type="number"
            value={localPetSeq}
            onChange={e => setLocalPetSeq(e.target.value)}
          />
        </FilterField>
        <FilterField label="ticketSeq" flex="flex-none" className="md:w-28">
          <TextInput
            type="number"
            value={localTicketSeq}
            onChange={e => setLocalTicketSeq(e.target.value)}
          />
        </FilterField>
        <FilterField label="errorCode" flex="flex-none" className="md:w-40">
          <TextInput
            placeholder="FAIL"
            value={localErrorCode}
            onChange={e => setLocalErrorCode(e.target.value)}
          />
        </FilterField>
        <FilterField label="traceId" flex="flex-1" className="md:min-w-[240px]">
          <TextInput
            placeholder="단건 추적 ID (no-dash UUID)"
            value={localTraceId}
            onChange={e => setLocalTraceId(e.target.value)}
          />
        </FilterField>
        <FilterField label="페이지 크기" flex="flex-none" className="md:w-24">
          <Select
            value={localSize}
            onChange={e => setLocalSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      {/* 정책 안내 (백엔드 명세 1.3 / 5.4) */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
        ※ 운영/개발 환경에서는 <strong>성공한 GET 요청은 기록되지 않습니다</strong> — 실패한
        GET 만 적재. POST/PUT/PATCH/DELETE 는 항상 성공·실패 모두 기록.
        이름 필터(보호자/사업자/사업장/펫)는 대소문자 무시 부분일치(LIKE).
        보존 기간: prod 7일 / dev 2일. <code>/api/admin/audit/**</code> 경로는
        자기참조 적재 폭주 방지를 위해 감사 로그 대상에서 제외됩니다.
      </div>

      <DataGrid<AuditLogListItem>
        columns={columns}
        data={rows}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        error={error}
        onRetry={() => refetch()}
        getRowId={row => String(row.logSeq)}
        onRowClick={row => setDetailLogSeq(row.logSeq)}
        emptyMessage="조건에 맞는 감사 로그가 없습니다."
        // apiSummary + URL 2줄 표시를 위해 row 높이 확장 (v1.4)
        rowHeight={56}
      />

      <Pagination
        pageIndex={pageIndex}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={size}
        onPageChange={setPageIndex}
      />

      <AuditLogDetailModal
        logSeq={detailLogSeq}
        onClose={() => setDetailLogSeq(null)}
      />
    </div>
  );
}
